import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { hash } from '@ember/helper';
import { service } from '@ember/service';
import type DocsService from '../services/kolay/docs.ts';
import type { Collection, Page } from '../services/kolay/types.ts';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import type { ComponentLike } from '@glint/template';
type InternalPageYield = {
    page: Page;
    Link: ComponentLike<{
        Element: HTMLAnchorElement;
        Blocks: {
            default: [page: Page, isActive: boolean];
        };
    }>;
};
export class PageNav extends Component<{
    /**
   * The `<nav>` element. It has a default `aria-label` of "Selected Group".
   * Normally an `aria-label` is not required,
   * but when there are multiple `<nav>` elements on a screen, it is required.
   */ Element: HTMLElement;
    Args: {
        /**
     * The class to apply to the `<a>` element when its link is active.
     *
     * Defaults to "active"
     */ activeClass?: string;
    };
    Blocks: {
        /**
     * If provided, this block will yield back the page for customizing the name and element.
     * By default the `name` property will be used in a link.
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:page as |page|>
     *       <x.Link>
     *         {{toSentenceCase page.name}}
     *       </x.Link>
     *     </:page>
     *   </PageNav>
     * </template>
     * ```
     */ page: [{
                page: Page;
                Link: ComponentLike<{
                    Element: HTMLAnchorElement;
                    Blocks: {
                        default: [page: Page, isActive: boolean];
                    };
                }>;
            }];
        /**
     * If provided, this block will yield back the collection for customizing the name.
     * By default the `name` property will be used or a link will be rendered if an index page is present..
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:collection as |x|>
     *       {{#if x.index}}
     *         <x.index.Link>
     *           {{sentenceCase x.collection.name}}
     *         </x.index.Link>
     *       {{else}}
     *         {{sentenceCase x.collection.name}}
     *       {{/if}}
     *     </:collection>
     *   </PageNav>
     * </template>
     * ```
     */ collection: [{
                collection: Collection;
                /**
         * If there is an index page, it'll be provided here,
         * and omitted from the :page block.
         */ index?: {
                    page: Page;
                    Link: ComponentLike<{
                        Element: HTMLAnchorElement;
                        Blocks: {
                            default: [page: Page, isActive: boolean];
                        };
                    }>;
                };
            }];
    };
}> {
    @service('kolay/docs')
    docs: DocsService;
    /**
   * Ember doesn't yet have a way to forward blocks,
   * so we have  to do this weird manualy forwarding ourselves
   *
   * This is extra annoying since Pages is a recursive component.
   */ static{
        template(`
    <nav aria-label='Selected Group' ...attributes>
      <Pages @item={{this.docs.tree}}>

        <:page as |p|>
          {{#if (has-block 'page')}}
            {{yield p to='page'}}
          {{else}}
            <p.Link>
              {{p.page.name}}
            </p.Link>
          {{/if}}
        </:page>

        <:collection as |c|>
          {{#if (has-block 'collection')}}
            {{yield c to='collection'}}
          {{else}}
            {{#if c.index}}
              <c.index.Link>
                {{c.index.page.name}}
              </c.index.Link>
            {{else}}
              {{c.collection.name}}
            {{/if}}
          {{/if}}
        </:collection>
      </Pages>
    </nav>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
function isCollection(x1: Page | Collection): x is Collection {
    return 'pages' in x1;
}
function isIndex(x1: Page | Collection) {
    if (isCollection(x1)) return false;
    return x1.path.endsWith('index.md');
}
function getIndexPage(x1: Collection): Page | undefined {
    let page1 = x1.pages.find(isIndex);
    if (page1 && isCollection(page1)) return;
    return page1;
}
const not = (x1: unknown)=>!x1;
const Pages: TOC<{
    Args: {
        item: Page | Collection;
        activeClass?: string;
    };
    Blocks: {
        page: [InternalPageYield];
        collection: [{
                collection: Collection;
                index?: InternalPageYield;
            }];
    };
}> = template(`
  {{#if (isCollection @item)}}
    <ul>
      {{#each @item.pages as |page|}}
        {{#if (not (isIndex page))}}
          <li>
            {{#if (isCollection page)}}

              {{! index.md pages can make the whole collection clickable }}
              {{#let (getIndexPage page) as |indexPage|}}
                {{#if indexPage}}
                  {{yield
                    (hash
                      collection=page
                      index=(hash
                        page=indexPage
                        Link=(component
                          PageLink item=indexPage activeClass=@activeClass
                        )
                      )
                    )
                    to='collection'
                  }}
                {{else}}
                  {{yield (hash collection=page) to='collection'}}
                {{/if}}
              {{/let}}
            {{/if}}

            <Pages @item={{page}}>
              <:page as |p|>{{yield p to='page'}}</:page>
              <:collection as |c|>{{yield c to='collection'}}</:collection>
            </Pages>
          </li>
        {{/if}}
      {{/each}}
    </ul>
  {{else}}
    {{yield
      (hash
        page=@item Link=(component PageLink item=@item activeClass=@activeClass)
      )
      to='page'
    }}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
let PageLink = class PageLink extends Component<{
    Element: HTMLAnchorElement;
    Args: {
        item: Page;
        activeClass?: string;
    };
    Blocks: {
        default: [page: Page, isActive: boolean];
    };
}> {
    @service
    router: RouterService;
    get activeClass() {
        return this.args.activeClass ?? 'active';
    }
    get isActive() {
        let subPath1 = this.args.item.path;
        if (subPath1 === '/') return false;
        return this.router.currentURL?.startsWith(subPath1) ?? false;
    }
    static{
        template(`
    <a
      href={{@item.path}}
      class={{if this.isActive this.activeClass}}
      ...attributes
    >{{yield @item this.isActive}}</a>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
};
//# sourceMappingURL=data:application/json;base64,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